import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { hash } from '@ember/helper';
import { service } from '@ember/service';
import type DocsService from '../services/kolay/docs.ts';
import type { Collection, Page } from '../services/kolay/types.ts';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import type { ComponentLike } from '@glint/template';
type InternalPageYield = {
    page: Page;
    Link: ComponentLike<{
        Element: HTMLAnchorElement;
        Blocks: {
            default: [page: Page, isActive: boolean];
        };
    }>;
};
export class PageNav extends Component<{
    /**
   * The `<nav>` element. It has a default `aria-label` of "Selected Group".
   * Normally an `aria-label` is not required,
   * but when there are multiple `<nav>` elements on a screen, it is required.
   */ Element: HTMLElement;
    Args: {
        /**
     * The class to apply to the `<a>` element when its link is active.
     *
     * Defaults to "active"
     */ activeClass?: string;
    };
    Blocks: {
        /**
     * If provided, this block will yield back the page for customizing the name and element.
     * By default the `name` property will be used in a link.
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:page as |page|>
     *       <x.Link>
     *         {{toSentenceCase page.name}}
     *       </x.Link>
     *     </:page>
     *   </PageNav>
     * </template>
     * ```
     */ page: [{
                page: Page;
                Link: ComponentLike<{
                    Element: HTMLAnchorElement;
                    Blocks: {
                        default: [page: Page, isActive: boolean];
                    };
                }>;
            }];
        /**
     * If provided, this block will yield back the collection for customizing the name.
     * By default the `name` property will be used or a link will be rendered if an index page is present..
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:collection as |x|>
     *       {{#if x.index}}
     *         <x.index.Link>
     *           {{sentenceCase x.collection.name}}
     *         </x.index.Link>
     *       {{else}}
     *         {{sentenceCase x.collection.name}}
     *       {{/if}}
     *     </:collection>
     *   </PageNav>
     * </template>
     * ```
     */ collection: [{
                collection: Collection;
                /**
         * If there is an index page, it'll be provided here,
         * and omitted from the :page block.
         */ index?: {
                    page: Page;
                    Link: ComponentLike<{
                        Element: HTMLAnchorElement;
                        Blocks: {
                            default: [page: Page, isActive: boolean];
                        };
                    }>;
                };
            }];
    };
}> {
    @service('kolay/docs')
    docs: DocsService;
    /**
   * Ember doesn't yet have a way to forward blocks,
   * so we have  to do this weird manualy forwarding ourselves
   *
   * This is extra annoying since Pages is a recursive component.
   */ static{
        template(`
    <nav aria-label='Selected Group' ...attributes>
      <Pages @item={{this.docs.tree}}>

        <:page as |p|>
          {{#if (has-block 'page')}}
            {{yield p to='page'}}
          {{else}}
            <p.Link>
              {{p.page.name}}
            </p.Link>
          {{/if}}
        </:page>

        <:collection as |c|>
          {{#if (has-block 'collection')}}
            {{yield c to='collection'}}
          {{else}}
            {{#if c.index}}
              <c.index.Link>
                {{c.index.page.name}}
              </c.index.Link>
            {{else}}
              {{c.collection.name}}
            {{/if}}
          {{/if}}
        </:collection>
      </Pages>
    </nav>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
function isCollection(x1: Page | Collection): x is Collection {
    return 'pages' in x1;
}
function isIndex(x1: Page | Collection) {
    if (isCollection(x1)) return false;
    return x1.path.endsWith('index.md');
}
function getIndexPage(x1: Collection): Page | undefined {
    let page1 = x1.pages.find(isIndex);
    if (page1 && isCollection(page1)) return;
    return page1;
}
const not = (x1: unknown)=>!x1;
const Pages: TOC<{
    Args: {
        item: Page | Collection;
        activeClass?: string;
    };
    Blocks: {
        page: [InternalPageYield];
        collection: [{
                collection: Collection;
                index?: InternalPageYield;
            }];
    };
}> = template(`
  {{#if (isCollection @item)}}
    <ul>
      {{#each @item.pages as |page|}}
        {{#if (not (isIndex page))}}
          <li>
            {{#if (isCollection page)}}

              {{! index.md pages can make the whole collection clickable }}
              {{#let (getIndexPage page) as |indexPage|}}
                {{#if indexPage}}
                  {{yield
                    (hash
                      collection=page
                      index=(hash
                        page=indexPage
                        Link=(component
                          PageLink item=indexPage activeClass=@activeClass
                        )
                      )
                    )
                    to='collection'
                  }}
                {{else}}
                  {{yield (hash collection=page) to='collection'}}
                {{/if}}
              {{/let}}
            {{/if}}

            <Pages @item={{page}}>
              <:page as |p|>{{yield p to='page'}}</:page>
              <:collection as |c|>{{yield c to='collection'}}</:collection>
            </Pages>
          </li>
        {{/if}}
      {{/each}}
    </ul>
  {{else}}
    {{yield
      (hash
        page=@item Link=(component PageLink item=@item activeClass=@activeClass)
      )
      to='page'
    }}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
let PageLink = class PageLink extends Component<{
    Element: HTMLAnchorElement;
    Args: {
        item: Page;
        activeClass?: string;
    };
    Blocks: {
        default: [page: Page, isActive: boolean];
    };
}> {
    @service
    router: RouterService;
    get activeClass() {
        return this.args.activeClass ?? 'active';
    }
    get isActive() {
        let subPath1 = this.args.item.path;
        if (subPath1 === '/') return false;
        return this.router.currentURL?.startsWith(subPath1) ?? false;
    }
    static{
        template(`
    <a
      href={{@item.path}}
      class={{if this.isActive this.activeClass}}
      ...attributes
    >{{yield @item this.isActive}}</a>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
};
//# sourceMappingURL=data:application/json;base64,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